import { useState, useCallback } from "react";
import Swal from "sweetalert2";
import { API_ENDPOINTS } from "../constants/repositoryConstants";

/**
 * Hook for handling repository file deletion
 * @param {Object} options - Hook options
 * @param {Function} options.onSuccess - Callback when delete completes successfully
 * @param {string} options.reportName - Optional report name override
 * @returns {Object} { showModal, itemToDelete, confirmDelete, executeDelete, hideModal, loading }
 */
const useRepositoryDelete = ({ onSuccess, reportName = null } = {}) => {
  const [showModal, setShowModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const getEffectiveReportName = useCallback(() => {
    if (reportName) {
      return reportName;
    }
    const storedReportName = localStorage.getItem("report_name");
    return storedReportName && storedReportName !== "undefined" ? storedReportName : "Repository";
  }, [reportName]);

  const confirmDelete = useCallback((name, customMessage = null) => {
    setItemToDelete(name);
    setDeleteMessage(customMessage || `Are you sure you want to delete this file ${name}?`);
    setShowModal(true);
  }, []);

  const hideModal = useCallback(() => {
    setShowModal(false);
    setItemToDelete(null);
    setDeleteMessage("");
  }, []);

  const executeDelete = useCallback(
    async (deleteName = itemToDelete) => {
      if (!deleteName) return;

      const companyid = localStorage.getItem("companyid");
      const username = localStorage.getItem("userName");
      const effectiveReportName = getEffectiveReportName();

      setLoading(true);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_API_URL}${API_ENDPOINTS.DELETE_FILE}`,
          {
            method: "POST",
            headers: {
              "Content-type": "application/json; charset=UTF-8",
              username: username,
            },
            body: JSON.stringify({
              company_id: companyid,
              file_names: [deleteName],
              report_name: effectiveReportName,
            }),
          }
        );

        if (response.status === 200) {
          const responseJson = await response.json();

          if (responseJson.success === false) {
            Swal.fire({
              width: 400,
              heightAuto: false,
              icon: "error",
              html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
              <p style="font-size: 16px;">${responseJson.data}</p>`,
              iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
            });
            hideModal();
          } else if (responseJson.status === 400) {
            Swal.fire({
              icon: "error",
              html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
              <p style="font-size: 16px;">${responseJson.title}</p>`,
              iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
            });
            hideModal();
          } else {
            hideModal();
            Swal.fire({
              icon: "success",
              html: '<h4 style="color:#407775;font-size: 20px;">Success!</h4><p style="font-size:16px;line-height: 20px;">Repository file deleted successfully</p>',
              iconHtml: '<img src="assets/img/successicon.png" style="width: 55px; height: 55px;" />',
            });
            if (onSuccess) {
              onSuccess();
            }
          }
        } else {
          Swal.fire({
            icon: "error",
            html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
            <p style="font-size: 16px;">Error in delete file</p>`,
            iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
          });
          hideModal();
        }
      } catch (err) {
        console.error(err);
        Swal.fire({
          icon: "error",
          html: `<h4 style="color: #CC2229; font-size: 20px; font-weight: bold;">Error!</h4>
          <p style="font-size: 16px;">An error occurred while deleting</p>`,
          iconHtml: '<img src="assets/img/error.png" style="width: 55px; height: 55px;" />',
        });
        hideModal();
      } finally {
        setLoading(false);
      }
    },
    [itemToDelete, getEffectiveReportName, hideModal, onSuccess]
  );

  return {
    showModal,
    itemToDelete,
    deleteMessage,
    confirmDelete,
    executeDelete,
    hideModal,
    loading,
  };
};

export default useRepositoryDelete;
